import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Immunizations subsection adapter
 * Handles vaccine/date pairs for various immunizations (13 fields)
 * This follows the MultiColumn pattern: vaccine + date pairs
 */
export class ImmunizationsAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Hepatitis B vaccine/date pair
    const hepB = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_HEP_B
    )?.at(0)

    const hepBDate = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_DATE
    )?.at(0)

    // Meningococcal vaccine/date pair
    const meningococcal = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_MENINGOCOCCAL
    )?.at(0)

    const meningococcalDate = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_DATE
    )?.at(0)

    // COVID-19 vaccine/date pair
    const covid19 = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_COVID_19
    )?.at(0)

    const covid19Date = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_DATE
    )?.at(0)

    // DTaP vaccine/date pair
    const dtap = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DTAP
    )?.at(0)

    const dtapDate = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DATE
    )?.at(0)

    // HPV vaccine/date pair
    const hpv = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_HPV
    )?.at(0)

    const hpvDate = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_DATE
    )?.at(0)

    // RSV vaccine/date pair
    const rsv = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_RSV
    )?.at(0)

    const rsvDate = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_DATE
    )?.at(0)

    // Notes field
    const notes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_NOTES
    )?.at(0)

    return {
      [SocFormField.IMMUNIZATIONS_HEP_B]: hepB,
      [SocFormField.IMMUNIZATIONS_HEP_B_DATE]: hepBDate,
      [SocFormField.IMMUNIZATIONS_MENINGOCOCCAL]: meningococcal,
      [SocFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE]: meningococcalDate,
      [SocFormField.IMMUNIZATIONS_COVID_19]: covid19,
      [SocFormField.IMMUNIZATIONS_COVID_19_DATE]: covid19Date,
      [SocFormField.IMMUNIZATIONS_DTAP]: dtap,
      [SocFormField.IMMUNIZATIONS_DTAP_DATE]: dtapDate,
      [SocFormField.IMMUNIZATIONS_HPV]: hpv,
      [SocFormField.IMMUNIZATIONS_HPV_DATE]: hpvDate,
      [SocFormField.IMMUNIZATIONS_RSV]: rsv,
      [SocFormField.IMMUNIZATIONS_RSV_DATE]: rsvDate,
      [SocFormField.IMMUNIZATIONS_NOTES]: notes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_HEP_B,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_HEP_B)
      },
      {
        linkId:
          SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_MENINGOCOCCAL,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_MENINGOCOCCAL)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_COVID_19,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_COVID_19)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DTAP,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_DTAP)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_HPV,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_HPV)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_RSV,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_RSV)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_NOTES,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_NOTES)
      }
    ]

    // Conditionally add date fields if they exist (they are conditionally rendered when vaccine === 'Yes')
    if (this.formDataFieldExists(SocFormField.IMMUNIZATIONS_HEP_B_DATE)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HEP_B_DATE,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_HEP_B_DATE)
      })
    }

    if (this.formDataFieldExists(SocFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_MENINGOCOCCAL_DATE,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_MENINGOCOCCAL_DATE)
      })
    }

    if (this.formDataFieldExists(SocFormField.IMMUNIZATIONS_COVID_19_DATE)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_COVID_19_DATE,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_COVID_19_DATE)
      })
    }

    if (this.formDataFieldExists(SocFormField.IMMUNIZATIONS_DTAP_DATE)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_DTAP_DATE,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_DTAP_DATE)
      })
    }

    if (this.formDataFieldExists(SocFormField.IMMUNIZATIONS_HPV_DATE)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_HPV_DATE,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_HPV_DATE)
      })
    }

    if (this.formDataFieldExists(SocFormField.IMMUNIZATIONS_RSV_DATE)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_IMMUNIZATIONS_RSV_DATE,
        value: this.getFormDataField(SocFormField.IMMUNIZATIONS_RSV_DATE)
      })
    }

    return mutations
  }
}
